import React, { useEffect, useMemo } from 'react';
import { getUvInsertConfigDescription } from '@shared/view/uv-inserts/get-config-description';
import { getUvInsertPriceRange } from '@shared/view/uv-inserts/get-price-display';
import { OptionSelectionPage } from '../../components/GiftWrapModal/InsertsPages/pages/OptionSelectionPage';
import {
  getPageOptions,
} from '../../context/pages';
import { usePage } from '../../context/PageContext';
import { useUvInsertsContext } from '../context/UvInsertsContext';
import { useLocale } from '../../context/LocaleContext';
import {
  getOptionVisibilityForOptions,
  getReachableConfigHandlesForClicksTo,
  resolveSinglePathDestination,
  type OptionNode,
} from '../navigation/option-tree';

type OptionSelectionRouteProps = {
  pageName: string;
};

/**
 * TanStack route wrapper around OptionSelectionPage.
 *
 * Computes MVVM-sourced subtitle overrides for every option on the page:
 *
 * 1. Finds all visualiser-route configs reachable from each option's clicksTo destination
 *    (works for both direct leaf routes and multi-step category routes).
 * 2. Derives a base description:
 *    - Single reachable config → uses config.description from uv-inserts-config.liquid via MVVM.
 *    - Multiple configs (category option) → uses the static subtitle from pages.js.
 * 3. Computes a price range across all reachable theme variants:
 *    - All same price → "(£4.99)"
 *    - Varies → "(From £4.99)"
 *    via getUvInsertPriceRange (pure @shared/view helper, no window access).
 * 4. Formats prices with the gift-wrap LocaleContext formatPrice.
 *
 * No window.giftWrapData reads at any layer.
 */
export function OptionSelectionRoute({ pageName }: OptionSelectionRouteProps) {
  const { resolved } = useUvInsertsContext();
  const { formatPrice } = useLocale();
  const { goToPage } = usePage();
  const options = useMemo(() => getPageOptions(pageName) as (OptionNode & { subtitle?: string })[], [pageName]);
  const configByHandle = useMemo<Map<string, (typeof resolved.configs)[number]>>(() => {
    if (!resolved) return new Map();
    return new Map(resolved.configs.map((config) => [config.handle, config]));
  }, [resolved]);

  const subtitleOverrides = useMemo<Record<number, string>>(() => {
    if (!resolved) return {};

    const overrides: Record<number, string> = {};

    options.forEach((option, index) => {
      const configHandles = getReachableConfigHandlesForClicksTo(option?.clicksTo);

      if (configHandles.length === 0) return;

      // Base description text:
      //   - One config → MVVM config.description (declared in uv-inserts-config.liquid).
      //   - Many configs → fall back to the static subtitle in pages.js (category-level copy).
      const mvvmDescription =
        configHandles.length === 1
          ? getUvInsertConfigDescription({ configHandle: configHandles[0], resolved })
          : null;

      const baseText: string = mvvmDescription ?? (option?.subtitle as string | undefined) ?? '';

      // Price range across all reachable theme variants (pure @shared/view call).
      const range = getUvInsertPriceRange({
        configs: resolved.configs,
        configHandles,
      });

      if (!baseText && !range) return;

      // Format price using the gift-wrap locale context (handles currency symbol + locale).
      const priceStr = range
        ? range.minPounds === range.maxPounds
          ? formatPrice({ amount: range.minPounds })
          : `From ${formatPrice({ amount: range.minPounds })}`
        : null;

      const subtitle = baseText
        ? priceStr
          ? `${baseText} (${priceStr})`
          : baseText
        : priceStr ?? '';

      if (subtitle) {
        overrides[index] = subtitle;
      }
    });

    return overrides;
  }, [resolved, formatPrice, options, configByHandle]);

  const optionVisibility = useMemo(() => {
    if (!resolved) {
      return {
        hiddenOptionIndexes: new Set<number>(),
        visibleOptionIndexes: options.map((_, index) => index),
      };
    }
    return getOptionVisibilityForOptions(options, configByHandle);
  }, [resolved, options, configByHandle]);

  const singlePathDestination = useMemo(() => {
    if (!resolved) return null;
    return resolveSinglePathDestination({ pageName, configByHandle });
  }, [resolved, pageName, configByHandle]);

  useEffect(() => {
    if (!singlePathDestination) return;
    goToPage(singlePathDestination);
  }, [singlePathDestination, goToPage]);

  if (singlePathDestination) {
    return null;
  }

  return (
    <OptionSelectionPage
      pageName={pageName}
      subtitleOverrides={subtitleOverrides}
      hiddenOptionIndexes={optionVisibility.hiddenOptionIndexes}
    />
  );
}
